/**
 * Preferences › Voice: the last three dictations anywhere (desktop), newest
 * first, with Copy and, where the AI step did not finish, Retry; marked
 * screenshots show as tiles under the text, each copied on its own. A
 * dictation of marks without words has no text to copy or retry. Fix a word
 * corrects a misheard word in place and teaches the vocabulary. Rust owns
 * the history; it is read when the page opens and whenever Rust says it
 * changed. The transcript is shown here and nowhere else: never logged or
 * reported.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Icons, PocketRow, PocketSection } from "@/components/ui";
import { connectedRuntimeIds } from "@/desktop/dictation-anywhere/connected-runtimes";
import { trackDictationAnywhereRetried } from "@/desktop/dictation-anywhere/dictation-anywhere-analytics";
import {
  copyDictationAnywhere,
  getDictationAnywhereHistory,
  retryDictationAnywhere,
  type DictationAnywhereDelivery,
  type DictationAnywhereRecord,
} from "@/desktop/dictation-anywhere/dictation-anywhere-native";
import { retryPromptFor } from "@/desktop/dictation-anywhere/polish-prompts";
import { listenWhileMounted } from "@/desktop/tauri-listener-cleanup";
import { formatRelativeTime, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { isTauri } from "@/lib/is-tauri";
import { useCompanionStore } from "@/stores/companion-store";
import { useDictationAnywhereSettingsStore } from "@/stores/dictation-anywhere-settings-store";
import { SettingsFieldError } from "./settings-text-field";
import { useDictationThumbnails } from "./use-dictation-thumbnails";
import { stopPromptName } from "./voice-dictation-keys";
import { VoiceDictationWordFix } from "./voice-dictation-word-fix";
import { VoiceRecentDictationImages } from "./voice-recent-dictation-images";
import { VoiceRecentDictationRecordings } from "./voice-recent-dictation-recordings";

const HISTORY_CHANGED = "dictation-anywhere-history-changed";
const SHOWN = 3;
const COPIED_MS = 1200;

const OUTCOME_KEY = {
  pasted: "outcomePasted",
  copied: "outcomeCopied",
  failed: "outcomeNotCopied",
  pending: "outcomeInProgress",
} as const satisfies Record<
  DictationAnywhereDelivery,
  keyof typeof settingsVoiceMessages.en
>;

const failed = (record: DictationAnywhereRecord) =>
  record.ai === "not_improved" || record.ai === "interrupted";

/** "<prompts>, <time>, <outcome>": the prompt names give way first. */
function DictationLine({ record }: { record: DictationAnywhereRecord }) {
  const t = useT(settingsVoiceMessages);
  const separator = t("listSeparator");
  const lead = failed(record)
    ? t("notImproved")
    : record.prompt_names.map(stopPromptName).join(separator);
  const rest = t("lineRest", {
    when: formatRelativeTime(record.created_at_ms, { style: "narrow" }),
    outcome: t(OUTCOME_KEY[record.delivery]),
  });
  return (
    <span className="flex min-w-0">
      {lead ? <span className="min-w-0 truncate">{lead}</span> : null}
      <span className="shrink-0 whitespace-pre">
        {lead ? `${separator}${rest}` : rest}
      </span>
    </span>
  );
}

function useDictationHistory() {
  const [records, setRecords] = useState<DictationAnywhereRecord[] | null>(
    null,
  );
  const latest = useRef(0);
  const refetch = useCallback(() => {
    const request = ++latest.current;
    getDictationAnywhereHistory().then(
      (next) => {
        if (request === latest.current) setRecords(next);
      },
      () => {
        if (request === latest.current) setRecords([]);
      },
    );
  }, []);
  useEffect(() => {
    if (!isTauri()) return;
    refetch();
    return listenWhileMounted(HISTORY_CHANGED, refetch);
  }, [refetch]);
  return { records, refetch };
}

export function VoiceRecentDictations() {
  const t = useT(settingsVoiceMessages);
  const tCommon = useT(commonMessages);
  const { records, refetch } = useDictationHistory();
  const defaultPrompt = useDictationAnywhereSettingsStore(
    (state) => state.defaultPrompt,
  );
  const prompts = useDictationAnywhereSettingsStore((state) => state.prompts);
  const runtimes = useCompanionStore((state) => state.availableRuntimes);
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fixingId, setFixingId] = useState<string | null>(null);
  const [fixedId, setFixedId] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  const shown = useMemo(
    () =>
      [...(records ?? [])]
        .sort((a, b) => b.created_at_ms - a.created_at_ms)
        .slice(0, SHOWN),
    [records],
  );
  const thumbnails = useDictationThumbnails(shown);
  if (!isTauri()) return null;

  const connected = connectedRuntimeIds(runtimes);
  // Retry reruns the prompt the dictation was stopped with, else Default's
  // text even with its switch off: asking for it is the request.
  const retryPrompt = (record: DictationAnywhereRecord) =>
    retryPromptFor({ defaultPrompt, prompts }, record.prompt_names);
  const retryBlocked = (record: DictationAnywhereRecord) =>
    retryPrompt(record) === null
      ? t("defaultHasNoInstructions")
      : connected.length === 0
        ? t("noAiTool")
        : null;

  // One "Copied" at a time, across text and images.
  function flashCopied(key: string) {
    setCopied(key);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(null), COPIED_MS);
  }

  async function copy(id: string, which: "original" | "improved") {
    setError(null);
    try {
      await copyDictationAnywhere(id, which);
      flashCopied(`${id}:${which}`);
    } catch {
      setError(t("copyFailed"));
    }
  }

  async function retry(record: DictationAnywhereRecord) {
    const prompt = retryPrompt(record);
    if (!prompt) return;
    const { id } = record;
    setError(null);
    setRetrying((current) => new Set(current).add(id));
    const startedAt = Date.now();
    const applied = { name: prompt.name, instruction: prompt.instruction };
    try {
      const report = await retryDictationAnywhere(id, [applied], connected);
      trackDictationAnywhereRetried(report, Date.now() - startedAt, prompt);
    } catch {
      trackDictationAnywhereRetried(null, Date.now() - startedAt, prompt);
      setError(t("retryFailed"));
    } finally {
      setRetrying((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      refetch();
    }
  }

  return (
    <PocketSection
      title={t("recentDictations")}
      testId="voice-recent-dictations"
    >
      {records !== null && shown.length === 0 ? (
        <p
          className="p-2 text-appkit-small text-muted"
          data-testid="voice-recent-dictations-empty"
        >
          {t("recentEmpty")}
        </p>
      ) : null}
      {shown.map((record) => {
        const hasText = record.raw !== "";
        const images = thumbnails(record.id);
        const recordings = record.screen_recordings ?? [];
        const hasRecordings =
          recordings.length > 0 || !!record.screen_recording_folder;
        const busy = record.ai === "pending" || retrying.has(record.id);
        const canRetry = hasText && failed(record) && !busy;
        const blocked = canRetry ? retryBlocked(record) : null;
        const copyLabel = (which: "original" | "improved", label: string) =>
          copied === `${record.id}:${which}` ? tCommon("copied") : label;
        const fixing = fixingId === record.id;
        const fixed = fixedId === record.id && !fixing;
        return (
          <PocketRow
            key={record.id}
            testId={`voice-recent-dictation-${record.id}`}
            leading={<Icons.Mic className="h-4 w-4" aria-hidden="true" />}
            title={
              fixing ? (
                <VoiceDictationWordFix
                  record={record}
                  onDone={() => {
                    setFixingId(null);
                    setFixedId(record.id);
                  }}
                  onCancel={() => setFixingId(null)}
                />
              ) : (
                // A transcript stays one line, where settings wrap their rows.
                <span className="block truncate">
                  {record.raw || (hasRecordings ? t("screenRecording") : "")}
                </span>
              )
            }
            secondLine={<DictationLine record={record} />}
            selected={fixing}
            below={
              images.length > 0 || hasRecordings || fixed ? (
                <>
                  {images.length > 0 ? (
                    <VoiceRecentDictationImages
                      historyId={record.id}
                      thumbnails={images}
                      copied={copied}
                      onCopied={flashCopied}
                      onError={setError}
                    />
                  ) : null}
                  <VoiceRecentDictationRecordings
                    historyId={record.id}
                    recordings={recordings}
                    recordingFolder={
                      record.screen_recording_folder ?? undefined
                    }
                    onError={setError}
                  />
                  {fixed ? (
                    <p
                      className="text-appkit-small text-muted"
                      data-testid={`voice-recent-fixed-${record.id}`}
                    >
                      {t("remembered")}
                    </p>
                  ) : null}
                </>
              ) : undefined
            }
            status={
              busy
                ? { tone: "working" }
                : failed(record)
                  ? { tone: "error" }
                  : undefined
            }
            hoverActions={
              (hasText || hasRecordings) && !fixing ? (
                <>
                  {blocked ? (
                    <span
                      data-testid={`voice-recent-retry-reason-${record.id}`}
                    >
                      {blocked}
                    </span>
                  ) : null}
                  {canRetry ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={blocked !== null}
                      onClick={() => void retry(record)}
                      data-testid={`voice-recent-retry-${record.id}`}
                    >
                      {t("retry")}
                    </Button>
                  ) : null}
                  {hasText ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setFixedId(null);
                        setFixingId(record.id);
                      }}
                      data-testid={`voice-recent-fix-${record.id}`}
                    >
                      {t("fixAWord")}
                    </Button>
                  ) : null}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void copy(record.id, "original")}
                    data-testid={`voice-recent-copy-${record.id}`}
                  >
                    {copyLabel("original", tCommon("copy"))}
                  </Button>
                  {record.enhanced ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void copy(record.id, "improved")}
                      data-testid={`voice-recent-copy-improved-${record.id}`}
                    >
                      {copyLabel("improved", t("copyImproved"))}
                    </Button>
                  ) : null}
                </>
              ) : undefined
            }
          />
        );
      })}
      <SettingsFieldError
        error={error}
        testId="voice-recent-dictations-error"
      />
    </PocketSection>
  );
}
